import * as R from 'ramda';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Button, Container, Table } from 'reactstrap';
import { withBorder } from '../../../reactutils/withBorder';
import { withLogin } from '../../../reactutils/withLogin';
import { withMenu } from '../../../reactutils/withMenu';
import { TabbedContent } from '../../../reactutils/withTabbedScreen';
import { withTitle } from '../../../reactutils/withTitle';
import { CacheView } from '../../../services/betting';
import { ButtonWithConfirmation } from '../../common/ButtonWithConfirmation';
import { MenuSysAdmin } from '../../MenuSysAdmin';
import { getSystemConfig, ServerType } from '../../System/systemConfig';
import { cacheClear, cacheClearAll, cacheLoad } from './actions';
import { cacheEntitySelector, cacheServerAvailableSelector } from './selectors';

export const getPathSegment = (path: string, pos: number): string => {
    const v = path.split('/'); 
    if (v.length > pos) {
        return decodeURIComponent(v[pos]);
    } else {
        return '';
    }
}

export const usePathSegment = (pos: number): string[] => {
    const location = useLocation();
    const [segment, setSegment] = React.useState<string>('');

    React.useEffect(() => {
        if (location && location.pathname) {
            setSegment(getPathSegment(location.pathname, pos));
        }
    }, [location, pos]);

    return [segment];
};

const CacheStatisticsComponent: React.FC<{}> = () => {
    const [serverName] = usePathSegment(3);
    const caches = useSelector((state: any) => cacheEntitySelector(state, serverName));
    const serverAvailable = useSelector((state: any) => cacheServerAvailableSelector(state, serverName));

    const dispatch = useDispatch();
    const onLoad = React.useCallback(() => {
        dispatch(cacheLoad(serverName as ServerType));
    }, [serverName, dispatch]);

    const onReload = () => {
        dispatch(cacheLoad(serverName as ServerType));
    };
    const onClearCache = (cache: string) => {
        dispatch(cacheClear({ serverName: serverName as ServerType, cache }));
    };
    const onClearAllCaches = () => {
        dispatch(cacheClearAll(serverName as ServerType));
    };

    React.useEffect(() => onLoad(), [onLoad, serverName, dispatch]);

    function renderCache(c: CacheView) {
        const avgTime = c.requestCount > 0 ? c.requestTime / c.requestCount : 0;
        const hitRate = c.requestCount > 0 ? (c.hitCount * 100.0) / c.requestCount : 0;
        const missRate = c.requestCount > 0 ? (c.missCount * 100.0) / c.requestCount : 0;
        return (
            <tr key={c.id}>
                <td>{c.name}</td>
                <td>{c.hitCount}</td>
                <td>{c.missCount}</td>
                <td>{avgTime.toLocaleString()}</td>
                <td>{c.requestTime}</td>
                <td>{c.deleteCount}</td>
                <td>{c.putCount}</td>
                <td>{hitRate.toLocaleString()}</td>
                <td>{missRate.toLocaleString()}</td>
                <td>{c.timeToLive}</td>
                <td>
                    <ButtonWithConfirmation
                        title="Clear cache"
                        message={`Are you sure that you want to clear caches '${c.name}'?`}
                        color="danger"
                        outline={true}
                        onOK={() => onClearCache(c.id)}
                    >
                        Clear
                    </ButtonWithConfirmation>
                </td>
            </tr>
        );
    }

    const tabNames = Object.keys(getSystemConfig().serverUrls);

    return (
        <TabbedContent activeTab={serverName} tabNames={tabNames} tabLinks={sysAdminCachePath}>
            <Container>
                <Button className="m-2" color="secondary" onClick={onReload} outline={false}>
                    Reload
                </Button>

                {serverAvailable && (
                    <ButtonWithConfirmation
                        title="Clear all caches"
                        message={`Are you sure that you want to clear all caches on server '${serverName}'`}
                        className="m-2"
                        color="danger"
                        onOK={onClearAllCaches}
                        outline={false}
                    >
                        Clear all
                    </ButtonWithConfirmation>
                )}
            </Container>
            {!serverAvailable && <p className="m-2">The server ist currently not available</p>}
            {serverAvailable && (
                <Table striped={true}>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Hit Count</th>
                            <th>Miss count</th>
                            <th>Avg Time (ms)</th>
                            <th>Total Time (ms)</th>
                            <th>Deletes</th>
                            <th>Puts</th>
                            <th>Hit Rate</th>
                            <th>Miss Rate</th>
                            <th>TTL</th>
                            <th>Clear</th>
                        </tr>
                    </thead>
                    <tbody>{caches && caches.map((c) => renderCache(c))}</tbody>
                </Table>
            )}
        </TabbedContent>
    );
};

export const sysAdminCachePath = (serverName: string) => `/sys/cache/${serverName}`;

export const CacheStatisticsScreen = R.compose(
    withMenu(<MenuSysAdmin />),
    withBorder(),
    withTitle('Caches'),
    withLogin()
)(CacheStatisticsComponent);
